export interface CaptionEvent {
  type: 'generated' | 'used' | 'edited' | 'cleared' | 'error';
  tone: 'friendly' | 'competitive';
  guesses: number;
  won: boolean;
  streak: number;
  captionLength: number;
  wasEdited: boolean;
  latencyMs: number;
  timestamp: number;
}

const STORAGE_KEY = 'wormble-caption-analytics';
const MAX_EVENTS = 50;

export function trackCaptionEvent(event: Omit<CaptionEvent, 'timestamp'>): void {
  try {
    const events = getCaptionEvents();
    events.push({ ...event, timestamp: Date.now() });
    
    // Keep last 100 events
    if (events.length > MAX_EVENTS) {
      events.shift();
    }
    
    localStorage.setItem(STORAGE_KEY, JSON.stringify(events));
  } catch (error) {
    console.error('Failed to track caption event:', error);
  }
}

export function getCaptionEvents(): CaptionEvent[] {
  try {
    const data = localStorage.getItem(STORAGE_KEY);
    return data ? JSON.parse(data) : [];
  } catch (error) {
    console.error('Failed to get caption events:', error);
    return [];
  }
}

export interface CaptionAnalytics {
  totalGenerated: number;
  totalUsed: number;
  totalEdited: number;
  totalCleared: number;
  totalErrors: number;
  useRate: number;
  editRate: number;
  avgLatency: number;
  toneDistribution: { friendly: number; competitive: number };
  errorRate: number;
}

export function getCaptionAnalytics(): CaptionAnalytics {
  const events = getCaptionEvents();
  
  const generatedEvents = events.filter(e => e.type === 'generated');
  const usedEvents = events.filter(e => e.type === 'used');
  const editedEvents = events.filter(e => e.type === 'edited');
  const clearedEvents = events.filter(e => e.type === 'cleared');
  const errorEvents = events.filter(e => e.type === 'error');
  
  const totalGenerated = generatedEvents.length;
  const totalUsed = usedEvents.length;
  const totalEdited = editedEvents.length;
  const totalCleared = clearedEvents.length;
  const totalErrors = errorEvents.length;
  
  const useRate = totalGenerated > 0 ? (totalUsed / totalGenerated * 100) : 0;
  const editRate = totalUsed > 0 ? (totalEdited / totalUsed * 100) : 0;
  const errorRate = totalGenerated > 0 ? (totalErrors / totalGenerated * 100) : 0;
  
  const avgLatency = generatedEvents.length > 0
    ? Math.round(generatedEvents.reduce((sum, e) => sum + e.latencyMs, 0) / generatedEvents.length)
    : 0;
  
  const friendlyCount = generatedEvents.filter(e => e.tone === 'friendly').length;
  const competitiveCount = generatedEvents.filter(e => e.tone === 'competitive').length;
  
  return {
    totalGenerated,
    totalUsed,
    totalEdited,
    totalCleared,
    totalErrors,
    useRate: Math.round(useRate),
    editRate: Math.round(editRate),
    avgLatency,
    toneDistribution: {
      friendly: friendlyCount,
      competitive: competitiveCount,
    },
    errorRate: Math.round(errorRate),
  };
}

export function clearCaptionAnalytics(): void {
  localStorage.removeItem(STORAGE_KEY);
}
